import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Sparkles, Loader2, Copy, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HistorySection from '../history/HistorySection';

interface Caption {
  text: string;
  copied?: boolean;
}

type Platform = 'instagram' | 'youtube' | 'twitter' | 'linkedin' | 'pinterest';
type Language = 'English' | 'Kannada';

const platformMeta: Record<Platform, { bg: string; icon: string; label: string }> = {
  instagram: { bg: 'from-[#54c4b6] to-[#216974]',   icon: '📱', label: 'Instagram' },
  youtube:   { bg: 'from-[#216974] to-[#216974]',        icon: '▶️', label: 'YouTube'   },
  twitter:   { bg: 'from-[#216974] to-[#54c4b6]',      icon: '𝕏',  label: 'Twitter/X' },
  linkedin:  { bg: 'from-[#216974] to-[#54c4b6]',      icon: '💼', label: 'LinkedIn'  },
  pinterest: { bg: 'from-[#216974] to-[#216974]',        icon: '📌', label: 'Pinterest' },
};

/** Parse "Title: ...\n\nDescription: ..." out of a YouTube caption blob */
function parseYouTubeCaption(raw: string): { title: string; description: string } | null {
  const titleMatch   = raw.match(/title\s*[:\-]\s*(.+)/i);
  const descMatch    = raw.match(/description\s*[:\-]\s*([\s\S]+)/i);
  if (!titleMatch) return null;
  return {
    title:       titleMatch[1].trim(),
    description: descMatch ? descMatch[1].trim() : '',
  };
}

const AICaptionGenerator = () => {
  const [topic, setTopic]         = useState('');
  const [platform, setPlatform]   = useState<Platform>('instagram');
  const [tone, setTone]           = useState('professional');
  const [language, setLanguage]   = useState<Language>('English');
  const [captions, setCaptions]   = useState<Caption[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!topic.trim()) { setError('Please enter a topic or content description'); return; }
    setGenerating(true);
    setError(null);
    setCaptions([]);

    try {
      const response = await fetch('/api/writing/captions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metadata: { description: topic },
          platform,
          tone,
          count: 5,
          language,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to generate captions');
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.captions)) {
        setCaptions(data.captions.map((text: string) => ({ text, copied: false })));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (index: number) => {
    navigator.clipboard.writeText(captions[index].text);
    setCaptions((prev) =>
      prev.map((c, i) => ({ ...c, copied: i === index ? true : c.copied }))
    );
    setTimeout(() => {
      setCaptions((prev) =>
        prev.map((c, i) => ({ ...c, copied: i === index ? false : c.copied }))
      );
    }, 2000);
  };

  const isYouTube = platform === 'youtube';

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center space-y-4">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#54c4b6] to-[#216974] rounded-2xl shadow-lg shadow-[#54c4b6]">
          <Zap size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">AI Caption Generator</h1>
        <p className="text-gray-500 text-lg max-w-lg mx-auto font-medium">
          Platform-optimised captions in seconds — 5 A/B variations every time.
        </p>
      </div>

      <Card className="overflow-hidden border-0 shadow-2xl shadow-[#216974]">
        <div className="p-8 space-y-6">
          {/* Content description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Content Description</label>
            <textarea
              placeholder="Describe the image, video, or content you want captions for..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent resize-none"
            />
          </div>

          {/* Platform selection */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">Platform</label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {(Object.entries(platformMeta) as [Platform, typeof platformMeta[Platform]][]).map(([p, meta]) => (
                <button
                  key={p}
                  onClick={() => setPlatform(p)}
                  className={`p-4 rounded-lg font-bold transition-all ${
                    platform === p
                      ? `bg-gradient-to-br ${meta.bg} text-white shadow-lg scale-105`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="text-2xl mb-1">{meta.icon}</div>
                  <div className="text-xs">{meta.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tone + Language + Generate */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Tone</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="trendy">Trendy</option>
                <option value="funny">Funny</option>
                <option value="inspiring">Inspiring</option>
                <option value="authoritative">Authoritative</option>
                <option value="empathetic">Empathetic</option>
                <option value="enthusiastic">Enthusiastic</option>
                <option value="informative">Informative</option>
                <option value="sarcastic">Sarcastic</option>
                <option value="serious">Serious</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
                <option value="informal">Informal</option>
                <option value="persuasive">Persuasive</option>
                <option value="educational">Educational</option>
                <option value="entertaining">Entertaining</option>
                <option value="nostalgic">Nostalgic</option>
                <option value="urgent">Urgent</option>
                <option value="mysterious">Mysterious</option>
                <option value="bold">Bold</option>
                <option value="minimalist">Minimalist</option>
                <option value="luxury">Luxury</option>
                <option value="playful">Playful</option>
                <option value="sincere">Sincere</option>
                <option value="witty">Witty</option>
                <option value="curious">Curious</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Language</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent"
              >
                <option value="English">English</option>
                <option value="Kannada">Kannada</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleGenerate}
                isLoading={generating}
                className="w-full h-10 bg-gradient-to-r from-[#54c4b6] to-[#216974] hover:scale-105 transition-transform text-white font-bold rounded-lg flex items-center justify-center gap-2"
                leftIcon={generating ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
              >
                {generating ? 'Generating...' : 'Generate Captions'}
              </Button>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-semibold"
              >
                ⚠️ {error}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>

      {/* Results */}
      <AnimatePresence>
        {captions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="space-y-4"
          >
            <h2 className="text-2xl font-bold text-gray-900">
              Generated {isYouTube ? 'YouTube Titles & Descriptions' : 'Captions'} — 5 Variations
            </h2>

            {captions.map((caption, index) => {
              const ytParsed = isYouTube ? parseYouTubeCaption(caption.text) : null;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  <Card className="border-0 shadow-lg p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-block bg-[#54c4b6]/10 text-[#216974] px-3 py-1 rounded-full text-sm font-bold">
                            Variation {index + 1}
                          </span>
                        </div>

                        {ytParsed ? (
                          <div className="space-y-2">
                            <div>
                              <p className="text-xs font-semibold text-[#216974] uppercase tracking-wider mb-1">Title</p>
                              <p className="text-gray-900 font-semibold leading-snug">{ytParsed.title}</p>
                            </div>
                            {ytParsed.description && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</p>
                                <p className="text-gray-700 text-sm leading-relaxed">{ytParsed.description}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <p className="text-gray-800 leading-relaxed text-base">{caption.text}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {caption.text.split(/\s+/).length} words &middot; {caption.text.length} chars
                            </p>
                          </>
                        )}
                      </div>

                      <Button
                        onClick={() => handleCopy(index)}
                        className={`flex-shrink-0 px-4 py-2 rounded-lg font-semibold transition-colors ${
                          caption.copied
                            ? 'bg-[#54c4b6]/10 text-[#216974]'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                        leftIcon={<Copy size={16} />}
                      >
                        {caption.copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <HistorySection toolId="ai-captions" />
    </div>
  );
};

export default AICaptionGenerator;
